import { NavLink, useNavigate, Link } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import {
  Sun,
  Inbox as InboxIcon,
  ListChecks,
  Calendar as CalendarIcon,
  HeartPulse,
  Library,
  FolderKanban,
  GraduationCap,
  Users,
  UserCircle2,
  Activity,
  ShieldCheck,
  Settings as SettingsIcon,
  LogOut,
  Wallet as WalletIcon,
  ChefHat,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { supabase } from "@/lib/supabase";

/**
 * SPEC 4.8 + 5.7:
 *  - 232px desktop fixed-left, --bg-2 background, hairline right border.
 *  - Brand mark "Haus" in Newsreader italic 500 with verdigris accent dot.
 *  - Nav rows: Inter Tight 500 at --type-body-s; active row = --chip-hi + accent icon.
 *  - User chip pinned at the bottom (per CLAUDE_DESIGN_BRIEF).
 *  - Click user chip → dropdown with View profile / Sign out.
 */

type NavRow = {
  to: string;
  label: string;
  icon: LucideIcon;
};

// BW12.5 Issue 2: Pantry / Recipes / Meal plan / Shopping consolidate into one
// "Kitchen" hub. Existing routes still resolve, so deep links and the in-app
// detail flows keep working.
const PRIMARY: NavRow[] = [
  { to: "/", label: "Today", icon: Sun },
  { to: "/inbox", label: "Inbox", icon: InboxIcon },
  { to: "/tasks", label: "Tasks", icon: ListChecks },
  { to: "/calendar", label: "Calendar", icon: CalendarIcon },
  { to: "/health", label: "Health", icon: HeartPulse },
  { to: "/kitchen", label: "Kitchen", icon: ChefHat },
  { to: "/finance", label: "Finance", icon: WalletIcon },
  { to: "/evidence", label: "Evidence", icon: Library },
  { to: "/projects", label: "Projects", icon: FolderKanban },
  { to: "/documents", label: "Documents", icon: ShieldCheck },
  { to: "/learning", label: "Learning", icon: GraduationCap },
  { to: "/people", label: "People", icon: UserCircle2 },
  { to: "/threads", label: "Threads", icon: Users },
  { to: "/integrations", label: "Integrations", icon: Activity },
  { to: "/settings", label: "Settings", icon: SettingsIcon },
];

export function Sidebar({ userLabel }: { userLabel?: string }) {
  return (
    <aside
      aria-label="Primary navigation"
      style={{
        width: "var(--sidebar-w)",
        background: "var(--bg-2)",
        borderRight: "1px solid var(--hair)",
        height: "100vh",
        position: "fixed",
        left: 0,
        top: 0,
        display: "flex",
        flexDirection: "column",
        padding: "var(--space-6) var(--space-4)",
        overflowY: "auto",
      }}
    >
      <BrandMark />

      <nav
        aria-label="Sections"
        style={{
          marginTop: "var(--space-6)",
          display: "flex",
          flexDirection: "column",
          gap: "var(--space-1)",
        }}
      >
        {PRIMARY.map((row) => (
          <SidebarRow key={row.to} {...row} />
        ))}
      </nav>

      <div style={{ flex: 1, minHeight: "var(--space-4)" }} />

      <UserChip label={userLabel ?? "Jonas"} />
    </aside>
  );
}

function BrandMark() {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "baseline",
        gap: "var(--space-2)",
        padding: "0 var(--space-2)",
      }}
    >
      <span
        style={{
          fontFamily: "var(--font-serif)",
          fontStyle: "italic",
          fontWeight: 500,
          fontSize: "26px",
          lineHeight: 1,
          letterSpacing: "-0.01em",
          color: "var(--fg)",
        }}
      >
        Haus
      </span>
      <span
        aria-hidden
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: "var(--accent)",
          display: "inline-block",
          transform: "translateY(-2px)",
        }}
      />
    </div>
  );
}

function SidebarRow({ to, label, icon: Icon }: NavRow) {
  return (
    <NavLink
      to={to}
      end={to === "/"}
      style={({ isActive }) => ({
        display: "flex",
        alignItems: "center",
        gap: "var(--space-3)",
        padding: "var(--space-2) var(--space-3)",
        borderRadius: "var(--radius-card)",
        fontFamily: "var(--font-sans)",
        fontWeight: 500,
        fontSize: "var(--type-body-s)",
        color: isActive ? "var(--fg)" : "var(--fg-2)",
        background: isActive ? "var(--chip-hi)" : "transparent",
        transition: `background var(--dur-fast) var(--ease-out), color var(--dur-fast) var(--ease-out)`,
        textDecoration: "none",
      })}
    >
      {({ isActive }) => (
        <>
          <Icon
            size={18}
            strokeWidth={1.5}
            color={isActive ? "var(--accent)" : "currentColor"}
          />
          <span>{label}</span>
        </>
      )}
    </NavLink>
  );
}

function UserChip({ label }: { label: string }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  async function handleSignOut() {
    setOpen(false);
    await supabase.auth.signOut();
    navigate("/sign-in", { replace: true });
  }

  return (
    <div ref={ref} style={{ position: "relative", marginTop: "var(--space-4)" }}>
      {open && (
        <div
          role="menu"
          style={{
            position: "absolute",
            bottom: "calc(100% + 6px)",
            left: 0,
            right: 0,
            background: "var(--bg-3)",
            border: "1px solid var(--hair-2)",
            borderRadius: "var(--radius-card)",
            boxShadow: "0 12px 40px rgba(0,0,0,0.18)",
            overflow: "hidden",
            zIndex: 30,
          }}
        >
          <Link
            to="/settings"
            role="menuitem"
            onClick={() => setOpen(false)}
            style={{
              display: "block",
              padding: "var(--space-2) var(--space-3)",
              fontSize: "var(--type-body-s)",
              color: "var(--fg)",
              textDecoration: "none",
            }}
          >
            View profile
          </Link>
          <button
            type="button"
            role="menuitem"
            onClick={handleSignOut}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--space-2)",
              width: "100%",
              padding: "var(--space-2) var(--space-3)",
              background: "transparent",
              border: "none",
              borderTop: "1px solid var(--hair)",
              fontSize: "var(--type-body-s)",
              color: "var(--severity-bad, #c53e2e)",
              cursor: "pointer",
              textAlign: "left",
            }}
          >
            <LogOut size={14} strokeWidth={1.5} />
            Sign out
          </button>
        </div>
      )}
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        style={{
          width: "100%",
          padding: "var(--space-3)",
          background: open ? "var(--chip-hi)" : "var(--chip)",
          border: "none",
          borderRadius: "var(--radius-card)",
          display: "flex",
          alignItems: "center",
          gap: "var(--space-3)",
          fontSize: "var(--type-body-s)",
          color: "var(--fg-2)",
          cursor: "pointer",
        }}
      >
        <div
          aria-hidden
          style={{
            width: 28,
            height: 28,
            borderRadius: "50%",
            background: "var(--accent-wash)",
            color: "var(--accent)",
            fontFamily: "var(--font-serif)",
            fontStyle: "italic",
            fontWeight: 500,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {label.slice(0, 1).toUpperCase()}
        </div>
        <span style={{ flex: 1, textAlign: "left" }}>{label}</span>
      </button>
    </div>
  );
}
